import React, { useState } from 'react';
import { Clock, LogIn, LogOut, Edit, Trash2, Pencil, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const TimeTrackingDashboard = ({ 
  user,
  timeStatus,
  timeEntries,
  timeReports,
  allEmployees,
  loading,
  onClockIn,
  onClockOut,
  onManualEntry,
  onUpdateEntry,
  onDeleteEntry,
  onRefresh
}) => {
  const [manualTimeForm, setManualTimeForm] = useState({
    date: new Date().toISOString().split('T')[0],
    clock_in_time: '08:30',
    clock_out_time: '18:30',
    break_duration: 1.5,
    reason: '',
    user_id: ''
  });

  const [editingEntry, setEditingEntry] = useState(null);
  const [editEntryForm, setEditEntryForm] = useState({
    date: '',
    clock_in_time: '',
    clock_out_time: '',
    break_duration: 0,
    reason: ''
  });
  const [deleteEntryConfirm, setDeleteEntryConfirm] = useState(null);

  const isManager = user?.role === 'manager' || user?.role === 'admin';

  const handleManualTimeEntry = async (e) => {
    e.preventDefault();
    const entryData = {
      ...manualTimeForm,
      break_duration: parseFloat(manualTimeForm.break_duration) || 0
    };
    
    const success = await onManualEntry(entryData);
    if (success) {
      setManualTimeForm({ 
        date: new Date().toISOString().split('T')[0],
        clock_in_time: '08:30', 
        clock_out_time: '18:30',
        break_duration: 1.5,
        reason: '', 
        user_id: '' 
      });
    }
  };

  const handleEditEntry = (entry) => {
    const clockIn = new Date(entry.clock_in);
    const clockOut = entry.clock_out ? new Date(entry.clock_out) : null;
    
    setEditingEntry(entry);
    setEditEntryForm({
      date: clockIn.toISOString().split('T')[0],
      clock_in_time: clockIn.toTimeString().slice(0, 5),
      clock_out_time: clockOut ? clockOut.toTimeString().slice(0, 5) : '',
      break_duration: 0,
      reason: entry.reason || ''
    });
  };

  const handleUpdateEntry = async () => {
    if (!editingEntry) return;
    
    const success = await onUpdateEntry(editingEntry.id, editEntryForm);
    if (success) {
      setEditingEntry(null);
      setEditEntryForm({ date: '', clock_in_time: '', clock_out_time: '', break_duration: 0, reason: '' });
    }
  };

  const handleDeleteEntry = async (entry) => {
    const success = await onDeleteEntry(entry.id);
    if (success) {
      setDeleteEntryConfirm(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', { 
      day: '2-digit', month: '2-digit', year: 'numeric' 
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('es-ES', { 
      hour: '2-digit', minute: '2-digit' 
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Time Entry Form Card */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Registrar Jornada
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleManualTimeEntry} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="manual-date">📅 Fecha</Label>
                  <Input
                    id="manual-date"
                    type="date"
                    value={manualTimeForm.date}
                    onChange={(e) => setManualTimeForm({...manualTimeForm, date: e.target.value})}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="clock-in-time">🕐 Entrada</Label>
                    <Input
                      id="clock-in-time"
                      type="time"
                      value={manualTimeForm.clock_in_time}
                      onChange={(e) => setManualTimeForm({...manualTimeForm, clock_in_time: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="clock-out-time">🕐 Salida</Label>
                    <Input
                      id="clock-out-time"
                      type="time"
                      value={manualTimeForm.clock_out_time}
                      onChange={(e) => setManualTimeForm({...manualTimeForm, clock_out_time: e.target.value})}
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="break-duration">☕ Pausa (horas)</Label>
                  <Input
                    id="break-duration"
                    type="number"
                    step="0.5"
                    min="0"
                    max="4"
                    value={manualTimeForm.break_duration}
                    onChange={(e) => setManualTimeForm({...manualTimeForm, break_duration: e.target.value})}
                    placeholder="Ej: 1.5 para 1h 30min"
                  />
                  <p className="text-xs text-slate-500 mt-1">Por defecto: 1.5h de almuerzo</p>
                </div>
                <div>
                  <Label htmlFor="reason">📝 Motivo (opcional)</Label>
                  <Input
                    id="reason"
                    value={manualTimeForm.reason}
                    onChange={(e) => setManualTimeForm({...manualTimeForm, reason: e.target.value})}
                    placeholder="Ej: Trabajo remoto"
                  />
                </div>
              </div>

              {isManager && allEmployees && allEmployees.length > 0 && (
                <div>
                  <Label htmlFor="entry-user">👤 Registrar para</Label>
                  <Select 
                    value={manualTimeForm.user_id || "myself"} 
                    onValueChange={(value) => setManualTimeForm({...manualTimeForm, user_id: value === "myself" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Mi registro (yo mismo)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="myself">Mi registro (yo mismo)</SelectItem>
                      {allEmployees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name} ({emp.department})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                <Plus className="h-4 w-4 mr-2" />
                {loading ? 'Registrando...' : 'Registrar Jornada'}
              </Button>
            </form>

            {/* Quick Clock Buttons */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-slate-500 mb-2">⚡ Fichaje Rápido (hora actual)</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onClockIn}
                  disabled={loading || timeStatus?.is_clocked_in}
                  className="flex-1"
                >
                  <LogIn className="h-3 w-3 mr-1" />
                  Entrada
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onClockOut}
                  disabled={loading || !timeStatus?.is_clocked_in}
                  className="flex-1"
                >
                  <LogOut className="h-3 w-3 mr-1" />
                  Salida
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resumen (30 días)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {timeReports ? (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Total Horas</span>
                  <span className="font-bold text-lg">{timeReports.total_hours_30_days}h</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Días Trabajados</span>
                  <span className="font-bold">{timeReports.days_worked}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Promedio/Día</span>
                  <span className="font-bold">{timeReports.average_daily_hours}h</span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Vacaciones</span>
                    <Badge variant="secondary">
                      {user.used_vacation_days}/{user.vacation_days} días
                    </Badge>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-slate-500">Cargando...</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Time Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Mis Registros</span>
            <Badge variant="outline">{timeEntries?.length || 0} registros</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {timeEntries && timeEntries.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Entrada</TableHead>
                  <TableHead>Salida</TableHead>
                  <TableHead>Horas</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {timeEntries.slice(0, 15).map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{formatDate(entry.clock_in)}</TableCell>
                    <TableCell>{formatTime(entry.clock_in)}</TableCell>
                    <TableCell>{entry.clock_out ? formatTime(entry.clock_out) : '-'}</TableCell>
                    <TableCell>
                      <Badge variant={entry.total_hours >= 8 ? "default" : "secondary"}>
                        {entry.total_hours?.toFixed(2) || '-'}h
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={entry.entry_type === 'manual' ? 'outline' : 'secondary'}>
                        {entry.entry_type === 'manual' ? 'Manual' : 'Auto'}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate" title={entry.reason}>
                      {entry.reason || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditEntry(entry)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteEntryConfirm(entry)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-slate-500 py-4">No hay registros</p>
          )}
        </CardContent>
      </Card>

      {/* Edit Entry Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Registro</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fecha</Label>
              <Input
                type="date"
                value={editEntryForm.date}
                onChange={(e) => setEditEntryForm({...editEntryForm, date: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hora Entrada</Label>
                <Input
                  type="time"
                  value={editEntryForm.clock_in_time}
                  onChange={(e) => setEditEntryForm({...editEntryForm, clock_in_time: e.target.value})}
                />
              </div>
              <div>
                <Label>Hora Salida</Label>
                <Input
                  type="time"
                  value={editEntryForm.clock_out_time}
                  onChange={(e) => setEditEntryForm({...editEntryForm, clock_out_time: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Pausa (horas)</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                value={editEntryForm.break_duration}
                onChange={(e) => setEditEntryForm({...editEntryForm, break_duration: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>Motivo</Label>
              <Input
                value={editEntryForm.reason}
                onChange={(e) => setEditEntryForm({...editEntryForm, reason: e.target.value})}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpdateEntry} disabled={loading} className="flex-1">
                Guardar Cambios
              </Button>
              <Button variant="outline" onClick={() => setEditingEntry(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteEntryConfirm} onOpenChange={() => setDeleteEntryConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que deseas eliminar este registro del{' '}
            {deleteEntryConfirm && formatDate(deleteEntryConfirm.clock_in)}?
          </p>
          <div className="flex gap-2 mt-4">
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteEntry(deleteEntryConfirm)}
              disabled={loading}
              className="flex-1"
            >
              Eliminar
            </Button>
            <Button variant="outline" onClick={() => setDeleteEntryConfirm(null)}>
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeTrackingDashboard;
